import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Alert,
  CircularProgress,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  Snackbar
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

const API_BASE_URL = 'https://genz-staging.feeltiptop.com';

const ReportCreate = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [feeds, setFeeds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [openModal, setOpenModal] = useState(false);
  const [patientName, setPatientName] = useState('');
  const [patientUserId, setPatientUserId] = useState(null);
  const [patientAge, setPatientAge] = useState('');
  const [patientGender, setPatientGender] = useState('');
  const [patientSearch, setPatientSearch] = useState('');
  const [submitStatus, setSubmitStatus] = useState({ open: false, success: true, message: '' });

  const getAuthHeaders = () => {
    const token = localStorage.getItem('authToken');
    return {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    };
  };

  // Fetch users on component mount
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_BASE_URL}/api/reports/users`, getAuthHeaders());
        if (response.data.success) {
          setUsers(response.data.users);
        }
      } catch (err) {
        setError('Failed to fetch users. Please try again.');
        console.error('Error fetching users:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  // Fetch feeds when user is selected
  useEffect(() => {
    const fetchFeeds = async () => {
      if (!selectedUser) {
        setFeeds([]);
        return;
      }

      try {
        setLoading(true);
        const response = await axios.get(
          `${API_BASE_URL}/api/reports/feeds?userId=${selectedUser.userId}`,
          getAuthHeaders()
        );
        if (response.data.success) {
          setFeeds(response.data.feeds);
        }
      } catch (err) {
        setError('Failed to fetch assessment feeds. Please try again.');
        console.error('Error fetching feeds:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchFeeds();
  }, [selectedUser]);

  const handleUserChange = (event) => {
    const user = users.find(u => u.userId === event.target.value);
    setSelectedUser(user || '');
    setError('');
  };

  const handleCreateReport = async (feed) => {
    try {
      navigate(`/ReportPage`, {
        state: {
          feedId: feed.feedId,
          userId: selectedUser.userId,
          userName: selectedUser.name,
          userEmail: selectedUser.email,
          feedName: feed.name,
          feedDate: feed.sessionDate
        }
      });
    } catch (err) {
      setError('Failed to create report. Please try again.');
      console.error('Error creating report:', err);
    }
  };

  // Patient onboarding submit handler
  const handlePatientSubmit = async (e) => {
    e.preventDefault();
    if (!patientUserId || !patientName || !patientAge || !patientGender) {
      setSubmitStatus({ open: true, success: false, message: 'Please fill all fields.' });
      return;
    }
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/patient-report/create`,
        {
          userId: patientUserId,
          fullName: patientName,
          age: patientAge,
          gender: patientGender
        },
        getAuthHeaders()
      );
      if (response.data.success) {
        setSubmitStatus({ open: true, success: true, message: 'Patient onboarded successfully!' });
        setOpenModal(false);
        setPatientName('');
        setPatientUserId(null);
        setPatientAge('');
        setPatientGender('');
      } else {
        setSubmitStatus({ open: true, success: false, message: response.data.message || 'Failed to onboard patient.' });
      }
    } catch (err) {
      setSubmitStatus({ open: true, success: false, message: 'Error onboarding patient.' });
    }
  };

  if (!localStorage.getItem('authToken')) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Please log in to access this page.
        </Alert>
      </Box>
    );
  }

  const genderOptions = [
    { value: 'M', label: 'Male' },
    { value: 'F', label: 'Female' },
    { value: 'Other', label: 'Other' }
  ];

  return (
    <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto', position: 'relative' }}>
      <Typography variant="h4" gutterBottom>
        Create Report
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <FormControl fullWidth sx={{ mb: 4 }}>
        <InputLabel>Select User</InputLabel>
        <Select
          value={selectedUser ? selectedUser.userId : ''}
          onChange={handleUserChange}
          label="Select User"
        >
          {users.map((user) => (
            <MenuItem key={user.userId} value={user.userId}>
              {user.name} ({user.email})
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {loading ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      ) : selectedUser && feeds.length > 0 ? (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Feed Name</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Assessments</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {feeds.map((feed) => (
                <TableRow key={feed.feedId}>
                  <TableCell>{feed.name}</TableCell>
                  <TableCell>
                    {new Date(feed.sessionDate).toLocaleDateString()}
                  </TableCell>
                  <TableCell>{feed.assessmentCount}</TableCell>
                  <TableCell align="right">
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => handleCreateReport(feed)}
                    >
                      Create Report
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : selectedUser ? (
        <Alert severity="info">No assessment feeds found for this user.</Alert>
      ) : null}

      {/* Floating Action Button */}
      <Fab color="primary" aria-label="add" sx={{ position: 'fixed', bottom: 32, right: 32, zIndex: 1000 }} onClick={() => setOpenModal(true)}>
        <AddIcon />
      </Fab>

      {/* Patient Onboarding Modal */}
      <Dialog open={openModal} onClose={() => setOpenModal(false)}>
        <DialogTitle>Patient Onboarding</DialogTitle>
        <form onSubmit={handlePatientSubmit}>
          <DialogContent>
            <Autocomplete
              options={users}
              getOptionLabel={(option) => option.name || ''}
              value={patientName ? { name: patientName } : null}
              onChange={(event, newValue) => {
                if (newValue) {
                  setPatientName(newValue.name);
                  setPatientUserId(newValue.userId);
                } else {
                  setPatientName('');
                  setPatientUserId(null);
                }
              }}
              renderInput={(params) => (
                <TextField {...params} label="Full Name" required margin="dense" />
              )}
              sx={{ mb: 2, mt: 1 }}
            />
            <TextField
              label="Age"
              type="number"
              value={patientAge}
              onChange={(e) => setPatientAge(e.target.value)}
              required
              fullWidth
              margin="dense"
              sx={{ mb: 2 }}
            />
            <FormControl fullWidth margin="dense" required>
              <InputLabel>Gender</InputLabel>
              <Select
                value={patientGender}
                label="Gender"
                onChange={(e) => setPatientGender(e.target.value)}
              >
                {genderOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenModal(false)}>Cancel</Button>
            <Button type="submit" variant="contained">Submit</Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Snackbar for feedback */}
      <Snackbar open={submitStatus.open} autoHideDuration={4000} onClose={() => setSubmitStatus({ ...submitStatus, open: false })}>
        <Alert elevation={6} variant="filled" severity={submitStatus.success ? 'success' : 'error'}>
          {submitStatus.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ReportCreate; 